import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { EditorRefPlugin } from '@lexical/react/LexicalEditorRefPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { QuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { LinkNode, AutoLinkNode } from '@lexical/link';
import { CodeNode } from '@lexical/code-core';
import { $createParagraphNode, $getRoot, type EditorState, type LexicalEditor } from 'lexical';
import { TEAMS_MESSAGE_MAX_LENGTH, teamsNodesToText, type TeamsBlockNode } from '@atlas/shared';
import { TeamsSendIcon } from '@atlas/ui';
import { composerContentSx, composerTheme } from './composerTheme';
import { lexicalToTeamsNodes } from './lexicalToTeamsNodes';
import { MentionNode } from './MentionNode';
import { TeamsComposerToolbar } from './TeamsComposerToolbar';
import { TeamsMentionPlugin } from './TeamsMentionPlugin';

/**
 * How close to the limit the counter appears. A permanent counter on a 4,000
 * character allowance is noise — nobody is near it in a sidebar chat.
 */
const COUNTER_VISIBLE_FROM = TEAMS_MESSAGE_MAX_LENGTH - 200;

/** Grows with the content, then scrolls — the panel is only ~320px wide. */
const MIN_HEIGHT = 40;
const MAX_HEIGHT = 160;

/**
 * Expanded mode, mirroring Teams' own compose-box toggle.
 *
 * Roughly three times the room, which is the point at which a multi-paragraph
 * message is visible while you write it rather than scrolling past the top. Not
 * larger: the thread above still has to be readable, and a compose box that eats
 * the conversation defeats the reason for writing in the panel at all.
 */
const EXPANDED_MIN_HEIGHT = 140;
const EXPANDED_MAX_HEIGHT = 420;

interface TeamsChatComposerProps {
  /**
   * Resolves when the message is accepted. It must REJECT on failure: the
   * composer only clears the editor on a resolved send, so a failed send leaves
   * the user's message exactly where they wrote it.
   *
   * `text` is the plain-text rendering (the optimistic echo and the length
   * check both use it); `nodes` is the document model. The client never builds
   * HTML — the server decides whether this document needs any.
   */
  onSend: (text: string, nodes: TeamsBlockNode[]) => Promise<void>;
  isSending: boolean;
  /** Message from the last failed send, rendered above the field. */
  errorMessage?: string;
  /** Called when the user edits after a failure, so the stale error clears. */
  onDismissError?: () => void;
  disabled?: boolean;
  /**
   * The chat being written in. Only the @mention popup uses it, to offer this
   * conversation's members rather than everyone the user talks to.
   */
  chatId?: string;
  /**
   * The chat's name, passed ONLY for a group chat — its presence is what offers
   * "mention everyone here" in the @ popup.
   */
  groupMentionLabel?: string;
  /**
   * Escape hatch to the underlying editor.
   *
   * Also what makes this component testable: Lexical does not process simulated
   * keystrokes under jsdom — it depends on `beforeinput` plus native selection,
   * neither of which jsdom implements, which is why upstream tests the typing
   * path with Playwright. Tests therefore seed the document through this ref and
   * then drive the toolbar and the send.
   */
  editorRef?: React.Ref<LexicalEditor>;
}

/**
 * The rich-text message composer.
 *
 * ## What it produces
 *
 * `TeamsBlockNode[]`, never HTML. The document model is the contract with the
 * backend (`packages/shared/src/teams-compose.ts`), and the one place markup is
 * assembled is server-side, from that model. A composer that built its own HTML
 * would be a composer that concatenates user text into markup destined for
 * other people's Teams clients — which is the whole thing this design exists to
 * make impossible.
 *
 * ## What it owns
 *
 * The draft, and nothing else. Sending, error state and the optimistic echo all
 * belong to the panel, exactly as before this became a rich editor.
 */
export function TeamsChatComposer(props: TeamsChatComposerProps) {
  // No `useTranslation` here on purpose: every string this component owns moved
  // inside `ComposerInner`, where the compose-mode state that decides them lives.
  const [initError, setInitError] = useState<string | null>(null);

  return (
    <Box
      sx={{
        flexShrink: 0,
        borderTop: 1,
        borderColor: 'divider',
        px: 1.5,
        py: 1,
        minWidth: 0,
      }}
    >
      <LexicalComposer
        initialConfig={{
          namespace: 'teams-chat-composer',
          theme: composerTheme,
          // Only the nodes the wire model can carry. Registering a HeadingNode
          // here would let a paste create one the serializer must then flatten.
          nodes: [
            QuoteNode,
            ListNode,
            ListItemNode,
            LinkNode,
            AutoLinkNode,
            CodeNode,
            // Must be registered even though nothing pastes one: an unregistered
            // node type throws on `importJSON`, so a mention would take the
            // whole editor down on any state restore rather than degrading.
            MentionNode,
          ],
          onError: (thrown) => {
            // Surfaced rather than swallowed: an editor fault here means the
            // user's message is not being represented correctly.
            setInitError(thrown.message);
            console.error('Teams composer error:', thrown);
          },
        }}
      >
        <ComposerInner {...props} initError={initError} onClearInitError={() => setInitError(null)} />
      </LexicalComposer>
    </Box>
  );
}

interface ComposerInnerProps extends TeamsChatComposerProps {
  initError: string | null;
  onClearInitError: () => void;
}

function ComposerInner({
  onSend,
  isSending,
  errorMessage,
  onDismissError,
  disabled = false,
  chatId,
  groupMentionLabel,
  editorRef,
  initError,
  onClearInitError,
}: ComposerInnerProps) {
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();
  const [nodes, setNodes] = useState<TeamsBlockNode[]>([]);
  /**
   * True while an IME (Japanese, Chinese, Korean) is mid-composition. Enter is
   * how those input methods COMMIT a candidate, so sending on it would fire off
   * half-finished text and swallow the keystroke the user meant.
   */
  const isComposingRef = useRef(false);
  /**
   * Expanded compose mode. Two changes, together, exactly as Teams pairs them:
   * a taller box, and **Enter stops sending** — it inserts a newline like any
   * other editor, and the send button becomes the only way to send.
   *
   * They belong together rather than as two settings. Someone reaches for the
   * taller box because they are writing something with paragraphs in it, and in
   * that mode Enter-sends is a trap: it fires off half a message mid-thought.
   */
  const [isExpanded, setIsExpanded] = useState(false);
  /**
   * True while the @mention popup is showing.
   *
   * A ref, not state, because it is read inside a capture-phase key handler and
   * must be current at the moment the key lands rather than at the last render.
   * While it is set, Enter belongs to the menu — it picks the highlighted
   * person. Sending the message on that keystroke would be the single most
   * annoying possible failure of this feature: you would fire off a half-typed
   * message every time you tried to mention someone.
   */
  const isMentionMenuOpenRef = useRef(false);
  const handleMentionOpenChange = useCallback((open: boolean) => {
    isMentionMenuOpenRef.current = open;
  }, []);

  // `disabled` has to reach the document itself, not just the buttons: a
  // contenteditable stays typeable however the surrounding chrome looks.
  useEffect(() => {
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  const text = teamsNodesToText(nodes);
  const trimmed = text.trim();
  const overBy = text.length - TEAMS_MESSAGE_MAX_LENGTH;
  const isTooLong = overBy > 0;
  const canSend = trimmed.length > 0 && !isTooLong && !isSending && !disabled;

  const handleChange = useCallback(
    (editorState: EditorState) => {
      setNodes(lexicalToTeamsNodes(editorState));
      if (errorMessage) onDismissError?.();
      if (initError) onClearInitError();
    },
    [errorMessage, initError, onClearInitError, onDismissError],
  );

  const submit = useCallback(() => {
    if (!canSend) return;
    // The nodes are re-derived from the live editor state rather than taken
    // from React state: a keystroke landing in the same tick as Enter would
    // otherwise be sent one render behind.
    const current = lexicalToTeamsNodes(editor.getEditorState());
    const currentText = teamsNodesToText(current);
    if (currentText.trim().length === 0) return;

    // Rejection is expected and handled by the panel's error prop; swallow it
    // here so a failed send is not also an unhandled promise rejection.
    void onSend(currentText, current).then(
      () => {
        editor.update(() => {
          $getRoot().clear().append($createParagraphNode());
        });
        setNodes([]);
      },
      () => undefined,
    );
  }, [canSend, editor, onSend]);

  /**
   * Enter sends; Shift+Enter is a new line.
   *
   * Handled in the CAPTURE phase on purpose. Lexical's own keydown listener is
   * attached to the contenteditable itself, so a bubbling handler would run
   * *after* Lexical had already inserted a paragraph. Capturing lets us stop the
   * event before it reaches the editor at all.
   */
  const handleKeyDownCapture = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Enter' || event.shiftKey) return;
      // The mention menu gets first refusal on Enter. Handing the key straight
      // back is what lets the typeahead's own listener — which is attached to
      // the editor, i.e. downstream of this capture handler — pick the person.
      if (isMentionMenuOpenRef.current) return;
      // Expanded mode hands Enter back to the editor untouched, so it breaks the
      // line like it would anywhere else.
      if (isExpanded) return;
      // `isComposing` is the standards-track signal; the ref covers browsers
      // that only fire the composition events.
      if (isComposingRef.current || event.nativeEvent.isComposing) return;
      event.preventDefault();
      event.stopPropagation();
      submit();
    },
    [isExpanded, submit],
  );

  const showCounter = text.length >= COUNTER_VISIBLE_FROM;
  const visibleError = errorMessage ?? initError ?? undefined;

  return (
    <Box
      component="form"
      onSubmit={(event: React.FormEvent) => {
        event.preventDefault();
        submit();
      }}
      sx={{ minWidth: 0 }}
    >
      {visibleError ? (
        <Alert
          severity="error"
          sx={{ mb: 1, py: 0, '& .MuiAlert-message': { minWidth: 0, overflowWrap: 'anywhere' } }}
          action={
            <Button color="inherit" size="small" onClick={submit} disabled={!canSend}>
              {t('teams.panel.retry')}
            </Button>
          }
        >
          <Typography variant="caption">{visibleError}</Typography>
        </Alert>
      ) : null}

      <TeamsComposerToolbar
        disabled={disabled}
        isExpanded={isExpanded}
        onToggleExpanded={() => setIsExpanded((value) => !value)}
      />

      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'flex-end', minWidth: 0 }}>
        <Box
          onKeyDownCapture={handleKeyDownCapture}
          onCompositionStart={() => {
            isComposingRef.current = true;
          }}
          onCompositionEnd={() => {
            isComposingRef.current = false;
          }}
          sx={{
            position: 'relative',
            flex: 1,
            minWidth: 0,
            border: 1,
            borderColor: isTooLong ? 'error.main' : 'divider',
            borderRadius: 1,
            bgcolor: disabled ? 'action.disabledBackground' : 'background.paper',
            ...composerContentSx,
          }}
        >
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                aria-label={t('teams.panel.composerLabel')}
                aria-multiline
                style={{
                  outline: 'none',
                  padding: 8,
                  minHeight: isExpanded ? EXPANDED_MIN_HEIGHT : MIN_HEIGHT,
                  maxHeight: isExpanded ? EXPANDED_MAX_HEIGHT : MAX_HEIGHT,
                  // Grows with the content, then scrolls; long unbroken strings
                  // wrap instead of widening the panel.
                  overflowY: 'auto',
                  overflowWrap: 'anywhere',
                }}
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          {/* The placeholder is a sibling text node, never
              `dangerouslySetInnerHTML` — nothing here ever writes HTML. */}
          {trimmed.length === 0 ? (
            <Typography
              variant="body2"
              color="text.disabled"
              aria-hidden
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                right: 8,
                pointerEvents: 'none',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {t('teams.panel.composerPlaceholder')}
            </Typography>
          ) : null}
        </Box>

        <Tooltip title={t('teams.panel.sendAriaLabel')}>
          {/* A disabled IconButton drops its tooltip and its focus, so the span
              keeps both reachable while the send is impossible. */}
          <span>
            <IconButton
              type="submit"
              color="primary"
              size="small"
              disabled={!canSend}
              aria-label={t('teams.panel.sendAriaLabel')}
              aria-busy={isSending}
            >
              {isSending ? <CircularProgress size={18} color="inherit" /> : <TeamsSendIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {/* The hint has to move with the mode: telling someone Enter sends when it
          no longer does is worse than showing no hint at all. */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 0.5, pt: 0.25 }}>
        {isExpanded ? t('teams.panel.composerHintExpanded') : t('teams.panel.composerHint')}
      </Typography>

      {isTooLong ? (
        <Typography variant="caption" color="error" sx={{ display: 'block', px: 0.5, pt: 0.25 }}>
          {t('teams.panel.messageTooLong', { count: overBy })}
        </Typography>
      ) : showCounter ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 0.5, pt: 0.25 }}>
          {t('teams.panel.charactersLeft', { count: TEAMS_MESSAGE_MAX_LENGTH - text.length })}
        </Typography>
      ) : null}

      <HistoryPlugin />
      <ListPlugin />
      <LinkPlugin />
      <TeamsMentionPlugin
        disabled={disabled}
        chatId={chatId}
        groupMentionLabel={groupMentionLabel}
        onOpenChange={handleMentionOpenChange}
      />
      <OnChangePlugin onChange={handleChange} ignoreSelectionChange />
      {editorRef ? <EditorRefPlugin editorRef={editorRef} /> : null}
    </Box>
  );
}
